import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export interface AvatarConfig {
  skin: string;
  cheeks: boolean;
  hairColor: string;
  hairFront: string;
  hairBack: string;
  face: string;
  shirtStyle: string;
  shirtColor: string;
  sleeveLength: string;
  pantsStyle: string;
  pantsColor: string;
  glasses: string;
  beard: string;
  accessory: string;
  /** Base64-encoded PNG snapshot of the rendered canvas. Populated on save. */
  snapshot: string;
}

/**
 * Native width of the modular character art space.
 * All head-layer SVGs (head, face, hair, beard, glasses) share a 145x143
 * artboard and are pre-aligned, so they all draw at art-space origin (0, 0).
 * @readonly
 */
const ART_W = 145;

/**
 * Native height of the modular character art space (head + body + legs).
 * @readonly
 */
const ART_H = 188;

/**
 * Art-space anchor positions for the body and limb layers.
 * Derived by compositing against the asset pack's official sample art.
 * Arm/sleeve pairs and leg/pants pairs share artboards, so each pair
 * draws at the same anchor.
 * @readonly
 */
const POS = {
  body: { x: 55, y: 108 },
  dress: { x: 52, y: 108 },
  armL: { x: 42, y: 112 },
  armR: { x: 91, y: 112 },
  legL: { x: 58, y: 122 },
  legR: { x: 74, y: 122 },
} as const;

/**
 * Career accessory overlays drawn as inline SVGs on the full 145x188
 * art space, so they render through the same pipeline as the pack assets.
 * Glasses are no longer an accessory here because the pack ships real
 * glasses as a dedicated head layer.
 * @readonly
 */
const ACCESSORY_SVGS: Record<string, string> = {
  grad_cap: `<svg width="145" height="188" viewBox="0 0 145 188" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M72.5 14L122 32L72.5 50L23 32Z" fill="#1a1a1a"/><rect x="50" y="38" width="45" height="12" rx="6" fill="#1a1a1a"/><circle cx="72.5" cy="32" r="4" fill="#2e2e2e"/><path d="M72.5 32 L104 36 L104 54" stroke="#FFD700" stroke-width="2.5" fill="none" stroke-linecap="round"/><circle cx="104" cy="58" r="4.5" fill="#FFD700"/></svg>`,
  hard_hat: `<svg width="145" height="188" viewBox="0 0 145 188" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M37 48 A35.5 30 0 0 1 108 48 Z" fill="#f1c40f"/><rect x="60" y="14" width="25" height="10" rx="5" fill="#f1c40f"/><rect x="24" y="44" width="97" height="9" rx="4.5" fill="#e6b800"/></svg>`,
  beret: `<svg width="145" height="188" viewBox="0 0 145 188" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="82" cy="34" rx="40" ry="17" transform="rotate(-8 82 34)" fill="#8B3a3a"/><rect x="46" y="42" width="58" height="8" rx="4" fill="#6a2a2a"/><circle cx="92" cy="20" r="4.5" fill="#6a2a2a"/></svg>`,
  stethoscope: `<svg width="145" height="188" viewBox="0 0 145 188" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M62 110 C58 118, 56 124, 60 130" stroke="#2c3e50" stroke-width="3.5" fill="none" stroke-linecap="round"/><path d="M83 110 C87 118, 89 124, 85 130" stroke="#2c3e50" stroke-width="3.5" fill="none" stroke-linecap="round"/><path d="M60 130 C62 134, 68 135, 72 133" stroke="#2c3e50" stroke-width="3.5" fill="none" stroke-linecap="round"/><circle cx="78" cy="132" r="6" fill="#7f8c8d" stroke="#2c3e50" stroke-width="2.5"/></svg>`,
  briefcase: `<svg width="145" height="188" viewBox="0 0 145 188" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="106" y="136" width="26" height="19" rx="3" fill="#8B5E3C"/><path d="M113 136 V132 a3 3 0 0 1 3-3 h6 a3 3 0 0 1 3 3 V136" stroke="#6B3F2A" stroke-width="2.5" fill="none"/><rect x="106" y="144" width="26" height="2" fill="#6B3F2A"/><rect x="116" y="142" width="6" height="6" rx="1" fill="#FFD700"/></svg>`,
  laptop: `<svg width="145" height="188" viewBox="0 0 145 188" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="52" y="112" width="41" height="26" rx="3" fill="#2c3e50"/><rect x="55" y="115" width="35" height="20" rx="2" fill="#3498db"/><rect x="48" y="138" width="49" height="5" rx="2.5" fill="#95a5a6"/></svg>`,
};

/**
 * Step-based avatar customization component. Composites modular character
 * SVG layers (nonemo asset pack) onto an HTML canvas in real time.
 *
 * The pack ships 3 skin tints and a single baked-in pants color, so this
 * component recolors SVG fills at load time: the tint_1 skin assets act as
 * a template for an 8-tone palette, and the pants/waistband fill (#545AF2)
 * is swapped for the selected pants color.
 */
@Component({
  selector: 'app-avatar-builder',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatSlideToggleModule,
  ],
  templateUrl: './avatar-builder.html',
  styleUrl: './avatar-builder.css',
})
export class AvatarBuilder implements OnInit, AfterViewInit {
  /** Initial avatar configuration passed in from the parent component. */
  @Input() initialConfig: Partial<AvatarConfig> | null = null;

  /** Emits the completed AvatarConfig (including snapshot) when the user saves. */
  @Output() saved = new EventEmitter<AvatarConfig>();

  /** Emits when the user cancels without saving. */
  @Output() cancelled = new EventEmitter<void>();

  /** Current step index in the multi-step customization flow. */
  currentStep = 1;

  /** Reference to the canvas element used for avatar rendering. */
  @ViewChild('avatarCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  /**
   * Base path to the modular character SVG assets.
   * @readonly
   */
  readonly BASE = 'assets/avatars/modular-characters/SVG';

  /** Live avatar configuration bound to the UI controls. */
  config: AvatarConfig = {
    skin: '1',
    cheeks: true,
    hairColor: 'brown',
    hairFront: 'regular',
    hairBack: 'short',
    face: 'smile',
    shirtStyle: 'trousers',
    shirtColor: 'blue',
    sleeveLength: 'short',
    pantsStyle: 'pants',
    pantsColor: 'blue',
    glasses: 'none',
    beard: 'none',
    accessory: 'none',
    snapshot: '',
  };

  /** Available skin tone option keys. */
  skins = ['1', '2', '3', '4', '5', '6', '7', '8'];

  /**
   * Recolor palettes per skin tone: base fill, inner-ear shade, and cheek
   * blush. Tones 1, 4, and 8 match the pack's shipped tint_1/2/3 exactly;
   * the rest are interpolated.
   */
  skinPalettes: Record<string, { base: string; ear: string; cheek: string }> = {
    '1': { base: '#FFC2A3', ear: '#FFA679', cheek: '#FFAB91' },
    '2': { base: '#F2B28D', ear: '#EE9367', cheek: '#E89B7C' },
    '3': { base: '#E29F78', ear: '#DC8455', cheek: '#D78A68' },
    '4': { base: '#D08269', ear: '#BE7159', cheek: '#C96F52' },
    '5': { base: '#B66E54', ear: '#9F5C45', cheek: '#AB5C42' },
    '6': { base: '#9A5A44', ear: '#874B37', cheek: '#8F4A33' },
    '7': { base: '#80493B', ear: '#6F3D2D', cheek: '#77402E' },
    '8': { base: '#673A34', ear: '#572D28', cheek: '#602F28' },
  };

  /** Available hair color keys, matching the asset pack filenames. */
  hairColors = ['black', 'blond', 'brown', 'red', 'orange', 'white', 'blue', 'green', 'violet'];

  /** Hex color swatches for each hair color, used in the UI picker. */
  hairColorSwatches: Record<string, string> = {
    black: '#1a1a1a',
    blond: '#FFE994',
    brown: '#823F30',
    red: '#FF3552',
    orange: '#FF6933',
    white: '#FFFFFF',
    blue: '#57F5FF',
    green: '#3DFA7E',
    violet: '#8F00FF',
  };

  /** Available front hair styles with display labels. */
  hairFronts = [
    { id: 'none', label: 'None' },
    { id: 'regular', label: 'Regular' },
    { id: 'straight', label: 'Straight' },
    { id: 'curly', label: 'Curly' },
    { id: 'elegant', label: 'Elegant' },
    { id: 'chupchik', label: 'Swoop' },
  ];

  /** Available back hair styles with display labels. */
  hairBacks = [
    { id: 'none', label: 'None' },
    { id: 'short', label: 'Short' },
    { id: 'long', label: 'Long' },
    { id: 'curly', label: 'Curly' },
  ];

  /** Available face expressions with display labels. */
  faces = [
    { id: 'smile', label: 'Smile' },
    { id: 'laughs', label: 'Laughing' },
    { id: 'cute', label: 'Cute' },
    { id: 'love', label: 'In Love' },
    { id: 'willing', label: 'Hopeful' },
    { id: 'gloating', label: 'Smug' },
    { id: 'sleepy', label: 'Sleepy' },
    { id: 'melancholic', label: 'Wistful' },
    { id: 'sad', label: 'Sad' },
    { id: 'crying', label: 'Crying' },
    { id: 'fright', label: 'Surprised' },
    { id: 'angry', label: 'Angry' },
    { id: 'tempered', label: 'Grumpy' },
  ];

  /** Available shirt styles with display labels. */
  shirtStyles = [
    { id: 'trousers', label: 'Shirt' },
    { id: 'dress', label: 'Dress' },
  ];

  /** Available shirt color keys, matching the asset pack filenames. */
  shirtColors = ['blue', 'green', 'orange', 'red', 'violet', 'white', 'black'];

  /** Hex color swatches for each shirt color, used in the UI picker. */
  shirtColorSwatches: Record<string, string> = {
    blue: '#57F5FF',
    green: '#3DFA7E',
    orange: '#FF6933',
    red: '#FF3552',
    violet: '#8F00FF',
    white: '#FFFFFF',
    black: '#1a1a1a',
  };

  /** Available sleeve lengths with display labels. */
  sleeveLengths = [
    { id: 'short', label: 'Short sleeves' },
    { id: 'long', label: 'Long sleeves' },
  ];

  /** Available pants styles with display labels. */
  pantsStyles = [
    { id: 'pants', label: 'Pants' },
    { id: 'shorts', label: 'Shorts' },
    { id: 'naked', label: 'None' },
  ];

  /** Available pants color keys. Applied by recoloring the baked-in fill. */
  pantsColors = ['blue', 'navy', 'black', 'grey', 'green', 'red', 'brown', 'violet'];

  /** Hex fills for each pants color; also used as UI swatches. */
  pantsColorSwatches: Record<string, string> = {
    blue: '#545AF2',
    navy: '#2C3E6B',
    black: '#1A1A1A',
    grey: '#9E9E9E',
    green: '#2ECC71',
    red: '#E74C3C',
    brown: '#8B5E3C',
    violet: '#8E44AD',
  };

  /** Available glasses options with display labels. */
  glassesOptions = [
    { id: 'none', label: 'None' },
    { id: 'round.black', label: 'Round · Black' },
    { id: 'round.white', label: 'Round · White' },
    { id: 'square.black', label: 'Square · Black' },
    { id: 'square.white', label: 'Square · White' },
  ];

  /** Available beard lengths with display labels. Beard color follows hair color. */
  beardOptions = [
    { id: 'none', label: 'None' },
    { id: 'short', label: 'Short' },
    { id: 'middle', label: 'Medium' },
    { id: 'long', label: 'Long' },
  ];

  /** Available career accessories with their display label and emoji icon. */
  accessories = [
    { id: 'none', label: 'None', icon: '✕' },
    { id: 'grad_cap', label: 'Grad Cap', icon: '🎓' },
    { id: 'hard_hat', label: 'Hard Hat', icon: '👷' },
    { id: 'beret', label: 'Beret', icon: '🎨' },
    { id: 'stethoscope', label: 'Stethoscope', icon: '⚕️' },
    { id: 'briefcase', label: 'Briefcase', icon: '💼' },
    { id: 'laptop', label: 'Laptop', icon: '💻' },
  ];

  /** Cache of loaded (and recolored) SVG part images, keyed by path + recolor map. */
  private partCache = new Map<string, Promise<HTMLImageElement | null>>();

  /** Monotonic token used to discard stale async renders. */
  private renderToken = 0;

  /** True when running in the browser (canvas/Image APIs are unavailable during SSR). */
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Applies any initial config passed in from the parent.
   * @returns {void}
   */
  ngOnInit(): void {
    if (this.initialConfig) {
      this.config = { ...this.config, ...this.initialConfig };
    }
  }

  /**
   * Sets canvas dimensions after the view is initialized and triggers the
   * first render. Width matches the element's rendered offset width; height
   * is fixed at 550px. No-op during server-side rendering.
   * @returns {void}
   */
  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    const canvas = this.canvasRef.nativeElement;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    this.drawAvatar();
  }

  /**
   * Loads an SVG part as an HTMLImageElement, optionally rewriting fill
   * colors first. Results are cached per path + recolor combination.
   * SVG text is fetched (or taken inline), recolored via string replacement,
   * and loaded through a base64 data URL so the canvas is never tainted.
   *
   * @param {string} source - Asset path relative to BASE, or 'inline:<key>' for accessory SVGs.
   * @param {Record<string, string>} [recolor] - Map of fill hex replacements, e.g. { '#545AF2': '#E74C3C' }.
   * @returns {Promise<HTMLImageElement | null>} The loaded image, or null if the asset failed to load.
   */
  private loadPart(
    source: string,
    recolor?: Record<string, string>,
  ): Promise<HTMLImageElement | null> {
    const key = source + (recolor ? JSON.stringify(recolor) : '');
    if (!this.partCache.has(key)) {
      const textPromise = source.startsWith('inline:')
        ? Promise.resolve(ACCESSORY_SVGS[source.slice(7)])
        : fetch(`${this.BASE}/${source}`).then((r) => {
            if (!r.ok) throw new Error(`${r.status} ${source}`);
            return r.text();
          });

      const imgPromise = textPromise
        .then((svg) => {
          if (recolor) {
            for (const [from, to] of Object.entries(recolor)) {
              svg = svg.split(from).join(to);
            }
          }
          return new Promise<HTMLImageElement | null>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = 'data:image/svg+xml;base64,' + btoa(svg);
          });
        })
        .catch((err) => {
          console.warn('missing avatar part:', source, err);
          return null;
        });

      this.partCache.set(key, imgPromise);
    }
    return this.partCache.get(key)!;
  }

  /**
   * Builds the fill replacement map that converts the tint_1 template
   * assets to the selected skin tone. Tone 1 needs no replacement.
   * @returns {Record<string, string> | undefined}
   */
  private skinRecolor(): Record<string, string> | undefined {
    if (this.config.skin === '1') return undefined;
    const p = this.skinPalettes[this.config.skin] ?? this.skinPalettes['1'];
    return { '#FFC2A3': p.base, '#FFA679': p.ear, '#FFAB91': p.cheek };
  }

  /**
   * Builds the fill replacement map for the selected pants color. Applied
   * to both the pants overlays and the trousers-style shirt, whose
   * waistband shares the same baked-in fill. The original blue needs no replacement.
   * @returns {Record<string, string> | undefined}
   */
  private pantsRecolor(): Record<string, string> | undefined {
    if (this.config.pantsColor === 'blue') return undefined;
    return { '#545AF2': this.pantsColorSwatches[this.config.pantsColor] ?? '#545AF2' };
  }

  /**
   * Maps the shirt color key to its sleeve asset color key.
   * The pack names the same color 'violet' for shirts but 'purple' for sleeves.
   * @returns {string}
   */
  private sleeveColorKey(): string {
    return this.config.shirtColor === 'violet' ? 'purple' : this.config.shirtColor;
  }

  /**
   * Clears and redraws the full avatar by compositing all SVG layers in
   * order (bottom to top): back hair, legs, pants, body, shirt, arms,
   * sleeves, head, face, beard, front hair, glasses, then accessory.
   *
   * All head layers share one pre-aligned artboard and draw at art-space
   * origin. Loading is async; a render token discards stale draws if the
   * config changes before all parts resolve.
   * @returns {Promise<void>}
   */
  async drawAvatar(): Promise<void> {
    if (!this.isBrowser || !this.canvasRef) return;
    const token = ++this.renderToken;
    const c = this.config;
    const skin = this.skinRecolor();
    const pants = this.pantsRecolor();

    const layers: { img: Promise<HTMLImageElement | null>; x: number; y: number }[] = [];
    const at = (img: Promise<HTMLImageElement | null>, x = 0, y = 0) => layers.push({ img, x, y });

    if (c.hairBack !== 'none') at(this.loadPart(`hairs/back/${c.hairBack}.${c.hairColor}.svg`));

    at(this.loadPart('skin/tint_1/leg.svg', skin), POS.legL.x, POS.legL.y);
    at(this.loadPart('skin/tint_1/leg.svg', skin), POS.legR.x, POS.legR.y);
    at(this.loadPart(`pants/${c.pantsStyle}.svg`, pants), POS.legL.x, POS.legL.y);
    at(this.loadPart(`pants/${c.pantsStyle}.svg`, pants), POS.legR.x, POS.legR.y);

    at(this.loadPart('skin/tint_1/body.svg', skin), POS.body.x, POS.body.y);
    const shirtPos = c.shirtStyle === 'dress' ? POS.dress : POS.body;
    at(this.loadPart(`shirts/${c.shirtStyle}.${c.shirtColor}.svg`, pants), shirtPos.x, shirtPos.y);

    at(this.loadPart('skin/tint_1/arm.svg', skin), POS.armL.x, POS.armL.y);
    at(this.loadPart('skin/tint_1/arm.svg', skin), POS.armR.x, POS.armR.y);
    at(
      this.loadPart(`sleeves/${this.sleeveColorKey()}.${c.sleeveLength}.svg`),
      POS.armL.x,
      POS.armL.y,
    );
    at(
      this.loadPart(`sleeves/${this.sleeveColorKey()}.${c.sleeveLength}.svg`),
      POS.armR.x,
      POS.armR.y,
    );

    at(this.loadPart(`skin/tint_1/${c.cheeks ? 'head.cheeks' : 'head'}.svg`, skin));
    at(this.loadPart(`faces/${c.face}.svg`));
    if (c.beard !== 'none') at(this.loadPart(`beards/${c.beard}.${c.hairColor}.svg`));
    if (c.hairFront !== 'none') at(this.loadPart(`hairs/front/${c.hairFront}.${c.hairColor}.svg`));
    if (c.glasses !== 'none') at(this.loadPart(`glasses/${c.glasses}.svg`));
    if (c.accessory !== 'none') at(this.loadPart(`inline:${c.accessory}`));

    const imgs = await Promise.all(layers.map((l) => l.img));
    if (token !== this.renderToken) return; // a newer render superseded this one

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sc = Math.min(canvas.width / ART_W, canvas.height / ART_H);
    const ox = (canvas.width - ART_W * sc) / 2;
    const oy = (canvas.height - ART_H * sc) / 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    imgs.forEach((img, i) => {
      if (!img) return;
      const l = layers[i];
      ctx.drawImage(img, ox + l.x * sc, oy + l.y * sc, img.width * sc, img.height * sc);
    });
  }

  /**
   * Redraws the canvas after any config change.
   * @returns {void}
   */
  onOptionChange(): void {
    this.drawAvatar();
  }

  /**
   * Captures the avatar as a base64 PNG cropped to the art bounds (not the
   * full canvas), attaches it to the config as `snapshot`, and emits the
   * completed AvatarConfig via the `saved` output.
   * @returns {void}
   */
  save(): void {
    const canvas = this.canvasRef.nativeElement;
    const sc = Math.min(canvas.width / ART_W, canvas.height / ART_H);
    const ox = (canvas.width - ART_W * sc) / 2;
    const oy = (canvas.height - ART_H * sc) / 2;

    const crop = document.createElement('canvas');
    crop.width = Math.round(ART_W * sc);
    crop.height = Math.round(ART_H * sc);
    const cctx = crop.getContext('2d');
    if (cctx) {
      cctx.drawImage(canvas, ox, oy, crop.width, crop.height, 0, 0, crop.width, crop.height);
    }
    const dataUrl = (cctx ? crop : canvas).toDataURL('image/png');
    this.saved.emit({ ...this.config, snapshot: dataUrl });
  }

  /**
   * Emits the cancelled event without saving any changes.
   * @returns {void}
   */
  cancel(): void {
    this.cancelled.emit();
  }
}
